/**
 * Smart ChatBot loader.
 * - If site settings configure a 3rd party widget (tawk/crisp/intercom/custom), inject its script.
 * - Otherwise, fall back to the built-in rule-based chatbot.
 */
import { useEffect, useState } from "react";
import { MessageSquare, X, Send } from "lucide-react";
import { api } from "@/lib/api";
import { useSettings } from "@/lib/settings";
import { toast } from "sonner";

const DEFAULT_TREE = {
  start: {
    bot: "Hi! I'm Tara Assist. Welcome to Tara Autohub. How can I help you today?",
    options: [
      { id: "buy", label: "I want to BUY equipment", next: "collect_lead" },
      { id: "sell", label: "I want to SELL my equipment", next: "collect_lead" },
      { id: "rent", label: "I want to RENT equipment", next: "collect_lead" },
      { id: "finance", label: "I need financing help", next: "collect_lead" },
    ],
  },
  collect_lead: { bot: "Perfect. Share your details and our team will call back within 2 hours.", form: true },
};

/** Build a dynamic chatbot tree from SiteSettings.chatbot_* fields. */
function buildTreeFromSettings(s) {
  if (!s) return DEFAULT_TREE;
  const opts = (s.chatbot_options && s.chatbot_options.length > 0) ? s.chatbot_options : null;
  if (!opts) return DEFAULT_TREE;
  const tree = {
    start: {
      bot: s.chatbot_welcome || DEFAULT_TREE.start.bot,
      options: opts.map((o, i) => ({ id: `opt${i}`, label: o.label || `Option ${i + 1}`, next: `reply_${i}` })),
    },
    collect_lead: { bot: s.chatbot_form_prompt || DEFAULT_TREE.collect_lead.bot, form: true },
    done: { bot: s.chatbot_thankyou || "Thank you!" },
  };
  opts.forEach((o, i) => {
    tree[`reply_${i}`] = {
      bot: o.reply || "Got it — please share your details.",
      options: [{ id: "ok", label: "Share my details", next: "collect_lead" }],
    };
  });
  return tree;
}

// ============== External widget loaders ==============
function loadTawk(propertyId, widgetId) {
  if (window.Tawk_API || !propertyId) return;
  window.Tawk_API = window.Tawk_API || {};
  window.Tawk_LoadStart = new Date();
  const s = document.createElement("script");
  s.async = true;
  s.src = `https://embed.tawk.to/${propertyId}/${widgetId || "default"}`;
  s.charset = "UTF-8";
  s.setAttribute("crossorigin", "*");
  s.dataset.taraChat = "tawk";
  document.body.appendChild(s);
}

function loadCrisp(websiteId) {
  if (window.$crisp || !websiteId) return;
  window.$crisp = [];
  window.CRISP_WEBSITE_ID = websiteId;
  const s = document.createElement("script");
  s.src = "https://client.crisp.chat/l.js";
  s.async = true;
  s.dataset.taraChat = "crisp";
  document.body.appendChild(s);
}

function loadIntercom(appId) {
  if (window.Intercom || !appId) return;
  window.intercomSettings = { app_id: appId };
  const s = document.createElement("script");
  s.src = `https://widget.intercom.io/widget/${appId}`;
  s.async = true;
  s.dataset.taraChat = "intercom";
  document.body.appendChild(s);
  // boot
  const i = function () { i.c(arguments); }; i.q = []; i.c = function (args) { i.q.push(args); };
  window.Intercom = window.Intercom || i;
}

function loadCustom(snippet) {
  if (!snippet) return;
  // Insert raw HTML/script into a hidden container, then re-run script tags
  const container = document.createElement("div");
  container.dataset.taraChat = "custom";
  container.innerHTML = snippet;
  document.body.appendChild(container);
  container.querySelectorAll("script").forEach((oldScript) => {
    const s = document.createElement("script");
    [...oldScript.attributes].forEach((a) => s.setAttribute(a.name, a.value));
    s.appendChild(document.createTextNode(oldScript.textContent || ""));
    oldScript.parentNode.replaceChild(s, oldScript);
  });
}

function clearExternalChat() {
  document.querySelectorAll('[data-tara-chat]').forEach((el) => el.remove());
  // hide tawk widget if API present
  try { if (window.Tawk_API && window.Tawk_API.hideWidget) window.Tawk_API.hideWidget(); } catch (e) {}
}

// ============== Built-in chatbot UI ==============
function BuiltInChat({ settings }) {
  const TREE = buildTreeFromSettings(settings);
  const buttonLabel = settings?.chatbot_button_label || "Tara Assist";
  const thankyou = settings?.chatbot_thankyou || "Thank you! Our team will reach out shortly.";
  const [open, setOpen] = useState(false);
  const [stepKey, setStepKey] = useState("start");
  const [history, setHistory] = useState([]);
  const [answers, setAnswers] = useState({});
  const [form, setForm] = useState({ name: "", phone: "", interest: "" });
  const [submitting, setSubmitting] = useState(false);

  const step = TREE[stepKey];

  const choose = (opt) => {
    setHistory((h) => [...h, { type: "user", text: opt.label }]);
    setAnswers((a) => ({ ...a, [stepKey]: opt.id }));
    setStepKey(opt.next);
  };

  const submitLead = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone) { toast.error("Name and phone are required"); return; }
    setSubmitting(true);
    try {
      await api.post("/leads/chat", {
        name: form.name,
        phone: form.phone,
        interest: form.interest || (answers.buy_category || answers.rent_category || "general"),
        answers,
      });
      setHistory((h) => [...h, { type: "bot", text: thankyou }]);
      setStepKey("done");
      toast.success("Lead captured — we'll be in touch!");
    } catch { toast.error("Could not submit. Please try again."); }
    finally { setSubmitting(false); }
  };

  const restart = () => { setStepKey("start"); setHistory([]); setAnswers({}); setForm({ name: "", phone: "", interest: "" }); };

  return (
    <>
      {!open && (
        <button
          onClick={() => setOpen(true)}
          className="fixed bottom-6 right-6 z-40 bg-[#E65C00] text-white w-14 h-14 flex items-center justify-center shadow-2xl hover:bg-[#CC5200] transition-all hover:scale-110"
          aria-label="Open chat"
          data-testid="chatbot-toggle"
        >
          <MessageSquare className="w-6 h-6" />
        </button>
      )}
      {open && (
        <div className="fixed bottom-6 right-6 z-40 w-[92vw] max-w-sm bg-white border border-[#1A1A1A] shadow-2xl" data-testid="chatbot-panel">
          <div className="bg-[#1A1A1A] text-white px-4 py-3 flex items-center justify-between">
            <div>
              <div className="font-heading font-black uppercase tracking-wide">{buttonLabel}</div>
              <div className="text-[10px] tracking-widest text-[#FFCC00]">ONLINE • USUALLY REPLIES INSTANTLY</div>
            </div>
            <button onClick={() => setOpen(false)} className="hover:text-[#FFCC00]" aria-label="Close chat" data-testid="chatbot-close">
              <X className="w-5 h-5" />
            </button>
          </div>
          <div className="p-4 max-h-[55vh] overflow-y-auto space-y-3 bg-gray-50">
            <div className="bg-white border border-gray-200 p-3 text-sm" data-testid="chatbot-welcome">{TREE.start.bot}</div>
            {history.map((m, i) => (
              <div key={i} className={`text-sm ${m.type === "user" ? "text-right" : ""}`}>
                <span className={`inline-block p-2.5 max-w-[80%] ${m.type === "user" ? "bg-[#E65C00] text-white" : "bg-white border border-gray-200"}`}>{m.text}</span>
              </div>
            ))}
            {step && step.bot && stepKey !== "start" && (<div className="bg-white border border-gray-200 p-3 text-sm">{step.bot}</div>)}
            {step && step.options && (
              <div className="flex flex-col gap-2 mt-2">
                {step.options.map((opt) => (
                  <button key={opt.id} onClick={() => choose(opt)} className="text-left text-sm border border-[#1A1A1A] px-3 py-2 hover:bg-[#1A1A1A] hover:text-white transition-colors" data-testid={`chatbot-option-${opt.id}`}>
                    {opt.label}
                  </button>
                ))}
              </div>
            )}
            {step && step.form && (
              <form onSubmit={submitLead} className="mt-2 space-y-2" data-testid="chatbot-lead-form">
                <input className="w-full border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:border-[#E65C00]" placeholder="Your name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="chatbot-name-input" required />
                <input className="w-full border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:border-[#E65C00]" placeholder="Mobile number" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="chatbot-phone-input" required />
                <button type="submit" disabled={submitting} className="btn-primary w-full justify-center text-sm" data-testid="chatbot-submit">
                  {submitting ? "Submitting..." : <><Send className="w-4 h-4" /> Send</>}
                </button>
              </form>
            )}
            {stepKey === "done" && (
              <button onClick={restart} className="text-xs text-[#E65C00] underline" data-testid="chatbot-restart">Start a new chat</button>
            )}
          </div>
        </div>
      )}
    </>
  );
}

// ============== Main export ==============
export default function ChatBot() {
  const { settings } = useSettings();
  const provider = settings?.chat_widget_provider || "builtin";
  const code = settings?.chat_widget_code || "";
  const key = settings?.chat_widget_key || "";

  useEffect(() => {
    // Clean up any prior external widget on provider change
    clearExternalChat();
    if (provider === "tawk" && key) {
      // key format: "propertyId/widgetId"
      const [pid, wid] = key.split("/");
      loadTawk(pid, wid);
    } else if (provider === "crisp" && key) {
      loadCrisp(key);
    } else if (provider === "intercom" && key) {
      loadIntercom(key);
    } else if (provider === "custom" && code) {
      loadCustom(code);
    }
    return () => clearExternalChat();
  }, [provider, code, key]);

  if (provider === "off") return null;
  if (provider === "builtin" || !provider) return <BuiltInChat settings={settings} />;
  // 3rd party widget injected via useEffect — render nothing here
  return null;
}
